import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import filterStore from 'store/filter/filter'
import { LoadingPlaceholderContainer } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { getPropertiesFromDigitalTwinModel } from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import { useSnapshot } from 'valtio'
import { AppliedSettingValues } from 'views/DigitalTwinCollectedSettingsView/components/AppliedSettingValues/AppliedSettingValues'
import {
  getPriorityLevels,
  toFollowupSettingsSelection,
  withFollowupAliasDefaults,
} from './followupAppliedSettings.helper'

type FollowupAppliedSettingValuesProps = {
  /** Names the `optjob_type` (and `optjob_subtype`); every other alias defaults from it and the system. */
  overrideAlias: UiConfigAliases | undefined
  /** Null while the follow-up page has no period yet. */
  period: { startTime: ISODateTime; endTime: ISODateTime } | null
}

export function FollowupAppliedSettingValues({ overrideAlias, period }: FollowupAppliedSettingValuesProps): ReactNode {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()
  const digitalTwinUid = activeSystem?.primary_digital_twin?.uid
  const { data: digitalTwin, isPending } = useDigitalTwin(digitalTwinUid)
  const { tagsPerUiconfig } = useSnapshot(filterStore)
  const availableFiltersSubset = tagsPerUiconfig.map((tags) => [...tags])
  const { activeTags, isActiveTagsFiltered } = useActiveTags({ availableFiltersSubset })
  const aliases = withFollowupAliasDefaults(overrideAlias, {
    subtypes: activeSystem?.subtypes,
    settingPriorityLevels: activeSystem?.setting_priority_levels,
  })
  const priorityLevels = getPriorityLevels(aliases)

  if (!period || (digitalTwinUid !== undefined && isPending)) {
    return <LoadingPlaceholderContainer height={100} />
  }
  if (!aliases || !digitalTwin?.model || !priorityLevels.length) {
    return <p>{t('Applied setting values could not be loaded')}</p>
  }

  const allProperties = getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model)
  const { selectedProperties, isFiltered } = toFollowupSettingsSelection({
    allProperties,
    priorityLevels,
    filterTags: [...activeTags],
    offeredTags: availableFiltersSubset,
    isHeaderFiltered: isActiveTagsFiltered,
  })

  return (
    <AppliedSettingValues
      period={period}
      overrideAlias={aliases}
      priorityLevels={priorityLevels}
      allProperties={allProperties}
      availableFiltersSubset={availableFiltersSubset}
      selectedPriorityLevels={priorityLevels}
      selectedProperties={selectedProperties}
      isFiltered={isFiltered}
      translations={digitalTwin.translations ?? {}}
    />
  )
}
